import { useQueryClient } from '@tanstack/react-query';
import { api, hooksApi } from '@verifime/api-definition';
import { reportPageActionToNewRelic } from '@verifime/utils';
import { useCallback } from 'react';
import { failureReason } from '../../WorkflowMetadataContext/workflowMetadataReporting';
import { isHeldDocumentTypeRefusal } from './heldDocumentTypeRefusal';
import { statusOf } from './httpStatus';
import {
  hasActivePermissionFor,
  PERMISSIONS_OF_CUSTOMER_KEY,
  type TPermissionOfCustomer,
} from './useSharedIdentityIds';

/**
 * The outcome of sharing, discriminated by a string (`outcome`) so it narrows in apps that compile
 * this source without strictNullChecks.
 *
 * - `shared`: every identity was granted to the client, and the requests have been refetched.
 * - `failed`: a grant failed; the ones after it were not tried. `status` is the HTTP status when
 *   there was a response, and absent when the server answered 200 but refused the grant.
 *   `nowShared` is true when every identity that was tried is now shared with the client, read
 *   again after the failure: the failure was then a grant that was not needed (#665). False when
 *   one is still not shared, or when that could not be read. `refusal` is set when person-api
 *   refused the grant because the customer holds a photo card or ImmiCard that the client has not
 *   opted into (409 with its `errorCode`): the caller tells the customer, in place of the generic
 *   server error.
 */
export type TShareIdentitiesResult =
  | { outcome: 'shared' }
  | {
      outcome: 'failed';
      status?: number;
      nowShared: boolean;
      refusal?: 'held-document-type-not-accepted';
    };

/** Not a union on a boolean: apps that compile this source without strictNullChecks cannot narrow it. */
type TGrantResult = {
  granted: boolean;
  status?: number;
  refusal?: 'held-document-type-not-accepted';
};

/** `PermissionResponse`: the permission made, or the reasons it was not. */
export type TPermissionResponse = {
  permission?: unknown;
  validationResults?: { resultType?: string }[];
};

const FAILED_LOG = 'Sharing an identity with the client failed';

/**
 * The New Relic page action for a grant that failed. The browser agent does not collect console
 * output in this deployment, so this is what engineering sees. It carries `source` (a constant
 * label) and `status` (a reason: an HTTP status, `network-error`, `grant-refused` or `no-client`):
 * never an id, a name, the error object or anything from a response.
 */
export const SHARE_FAILED_PAGE_ACTION = 'share-identities-failed';

const reportFailure = (reason: string) =>
  reportPageActionToNewRelic(SHARE_FAILED_PAGE_ACTION, {
    source: 'share-identities',
    status: reason,
  });

/**
 * `POST /v1/permission` answers 200 on a validation error, with no permission and the errors in
 * `validationResults`. A grant is made only when it has a permission and no error.
 */
export const isGranted = (response: TPermissionResponse | undefined): boolean =>
  !!response?.permission &&
  !(response.validationResults ?? []).some((result) => result.resultType === 'Error');

/** The failure is logged by its status or its reason only: the request and response carry ids. */
async function grant(customerId: string, organisationId: string): Promise<TGrantResult> {
  try {
    const response = await api.postV1permission({ customerId, organisationId });
    if (isGranted(response as TPermissionResponse)) {
      return { granted: true };
    }
    console.error(FAILED_LOG, { reason: 'grant-refused' });
    reportFailure('grant-refused');
    return { granted: false };
  } catch (error) {
    const status = statusOf(error);
    console.error(FAILED_LOG, { status });
    reportFailure(failureReason(error));
    return {
      granted: false,
      status,
      ...(isHeldDocumentTypeRefusal(error) && {
        refusal: 'held-document-type-not-accepted' as const,
      }),
    };
  }
}

/**
 * Grants the customer's own identities to one client (`POST /v1/permission`), one after another. It
 * stops at the first failure. The caller shares only after the choice is confirmed, because the
 * grant closes the customer's open requests for the client and they must exist to be closed.
 *
 * On success the permission requests are refetched, and awaited, so the caller routes on the
 * server's requests. On failure what is shared is read again too: an identity granted before the
 * failure must not be granted a second time by a retry.
 */
export function useShareIdentities(organisationId: string) {
  const queryClient = useQueryClient();

  const refreshRequests = useCallback(
    () =>
      queryClient.invalidateQueries(hooksApi.getKeyByAlias('getV1permissionrequestme'), {
        refetchType: 'all',
      }),
    [queryClient],
  );

  /**
   * Whether every identity is shared with the client in the shared set as it was just read again.
   * An identity whose read is not held (it was not read, or the read failed) is not shared: a
   * failure is never explained away on a guess.
   */
  const areAllShared = useCallback(
    (customerIds: string[]) =>
      customerIds.every((customerId) =>
        hasActivePermissionFor(
          queryClient.getQueryData<TPermissionOfCustomer[]>([
            PERMISSIONS_OF_CUSTOMER_KEY,
            customerId,
          ]),
          organisationId,
        ),
      ),
    [organisationId, queryClient],
  );

  const share = useCallback(
    async (customerIds: string[]): Promise<TShareIdentitiesResult> => {
      // A grant is never made to a client that is not known.
      if (!organisationId) {
        console.error(FAILED_LOG, { reason: 'no-client' });
        reportFailure('no-client');
        return { outcome: 'failed', nowShared: false };
      }
      const tried: string[] = [];
      for (const customerId of customerIds) {
        tried.push(customerId);
        const result = await grant(customerId, organisationId);
        if (!result.granted) {
          await queryClient.invalidateQueries([PERMISSIONS_OF_CUSTOMER_KEY]);
          await refreshRequests();
          return {
            outcome: 'failed',
            ...(result.status !== undefined && { status: result.status }),
            nowShared: areAllShared(tried),
            ...(result.refusal && { refusal: result.refusal }),
          };
        }
      }
      await refreshRequests();
      return { outcome: 'shared' };
    },
    [areAllShared, organisationId, queryClient, refreshRequests],
  );

  return { share };
}
